/**
 * Small fetch wrapper for the portal API.
 * - Same-origin requests with cookies (session cookie is HttpOnly).
 * - CSRF double-submit: fetches a token once and sends it on every unsafe request; on a
 *   csrf_failed response it refreshes the token and retries once.
 * - Errors are normalized to ApiError with a user-safe message and per-field messages.
 */

export interface FieldError {
  field: string;
  message: string;
}

export class ApiError extends Error {
  status: number;
  code: string;
  fields: FieldError[];
  requestId?: string;

  constructor(
    status: number,
    code: string,
    message: string,
    fields: FieldError[] = [],
    requestId?: string,
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.fields = fields;
    this.requestId = requestId;
  }
}

const BASE = "/api/v1";
let csrfToken: string | null = null;

export async function ensureCsrf(force = false): Promise<string> {
  if (csrfToken && !force) return csrfToken;
  const res = await fetch(`${BASE}/auth/csrf`, { credentials: "same-origin" });
  const data = (await res.json()) as { csrf_token: string };
  csrfToken = data.csrf_token;
  return csrfToken;
}

export function resetCsrfForTests() {
  csrfToken = null;
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RequestOptions {
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  rawBody?: BodyInit;
}

async function parseError(res: Response): Promise<ApiError> {
  let data: {
    error?: { code?: string; message?: string; fields?: FieldError[] };
    request_id?: string;
  } = {};
  try {
    data = await res.json();
  } catch {
    /* non-JSON error */
  }
  const fallback =
    res.status >= 500
      ? "Something went wrong on our side. Please try again."
      : "We could not complete that request.";
  return new ApiError(
    res.status,
    data.error?.code ?? "error",
    data.error?.message ?? fallback,
    data.error?.fields ?? [],
    data.request_id,
  );
}

export async function request<T>(
  method: Method,
  path: string,
  opts: RequestOptions = {},
  retried = false,
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json", ...opts.headers };
  if (method !== "GET") headers["X-CSRF-Token"] = await ensureCsrf();
  let body: BodyInit | undefined = opts.rawBody;
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body,
      credentials: "same-origin",
      signal: opts.signal,
    });
  } catch (err) {
    if ((err as Error).name === "AbortError") throw err;
    throw new ApiError(
      0,
      "network_error",
      "We could not reach the portal. Check your connection and try again.",
    );
  }
  if (!res.ok) {
    const err = await parseError(res);
    if (err.code === "csrf_failed" && !retried) {
      await ensureCsrf(true);
      return request<T>(method, path, opts, true);
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const type = res.headers.get("content-type") ?? "";
  return (type.includes("application/json") ? await res.json() : await res.text()) as T;
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>("GET", path, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>("POST", path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>("PUT", path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>("PATCH", path, { ...opts, body }),
  del: <T>(path: string, opts?: RequestOptions) => request<T>("DELETE", path, opts),
};

/** Map API field errors onto a react-hook-form `setError`. */
export function applyFieldErrors(
  err: unknown,
  setError: (name: never, error: { type: string; message: string }) => void,
): boolean {
  if (!(err instanceof ApiError) || err.fields.length === 0) return false;
  for (const f of err.fields) setError(f.field as never, { type: "server", message: f.message });
  return true;
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong. Please try again.";
}
